import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  ContentLayout,
  Header as AWSHeader,
  Icon,
  Link,
  SpaceBetween,
  StatusIndicator
} from '@amzn/awsui-components-console';
import {makeApiCall} from '../../utils/api';
import {API_PATHS} from '../../config/api-endpoints';
import {useProfile} from '../../hooks/useProfiles';
import StepFunctionsGraph from '../molecules/StepFunctionsGraph';
import JsonDataViewer from '../molecules/JsonDataViewer';
import NProgress from 'nprogress';
import './JobHistoryOverview.scss';

type JobRunStatus =
    | 'PENDING'
    | 'GENERATING_CODE'
    | 'SUBMITTING_QUERY'
    | 'QUERY_SUBMITTED'
    | 'QUERY_RUNNING'
    | 'COMPLETED'
    | 'CODE_RETRIEVED'
    | 'FAILED'
    | 'SUBMISSION_FAILED'
    | 'QUERY_FAILED'
    | 'MONITORING_FAILED'
    | 'RETRIEVAL_FAILED'
    | 'RETRYING'
    | 'FAILED_MAX_RETRIES'
    | 'SUCCEEDED';

interface JobRunDetails {
  jobId: string;
  runId: string;
  jobName?: string;
  status: JobRunStatus;
  runType?: 'NORMAL' | 'ADHOC';
  startedAt: string;
  updatedAt?: string;
  duration?: string;
  templateId?: string;
  templateUri?: string;  // S3 URI of the template
  mermaidDiagram?: string;  // Mermaid diagram string from backend
  aiSummary?: string;  // AI-generated summary from backend
  createdBy?: string;
  ldap?: string;  // LDAP group for this run
  feedback?: string;
  executionArn?: string;  // Step Functions execution ARN
  codeGenerationOutput?: {  // Dynamic map of output data
    sqlPath?: string;
    outputLocation?: string;
    timestamp?: string;
    [key: string]: any;  // Allow dynamic properties
  };
  errorMessage?: string | null;  // Error message if failed
  currentAttempt?: number;  // Current retry attempt number
  executionHistory?: string;  // ASL JSON for Step Functions visualization
  executionStatus?: string;  // RUNNING | SUCCEEDED | FAILED | TIMED_OUT | ABORTED
}

interface JobInfo {
  jobId: string;
  jobName: string;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  ldap?: string;
  jobType?: string;
  mermaidDiagram?: string;  // Mermaid diagram from GetJob API
  aiSummary?: string;  // AI-generated summary from GetJob API
}


interface  ExecutonHistory {
  definition: any;
  executionHistory: any;
}


// Failure statuses
const FAILURE_STATUSES: JobRunStatus[] = [
  'FAILED',
  'SUBMISSION_FAILED',
  'QUERY_FAILED',
  'MONITORING_FAILED',
  'FAILED_MAX_RETRIES',
  'RETRIEVAL_FAILED'
];

// Helper function to check if status is a failure
const isFailureStatus = (status: JobRunStatus): boolean => {
  return FAILURE_STATUSES.includes(status);
};

// Helper function to calculate duration between two timestamps
const calculateDuration = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) return 'N/A';

  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const durationMs = end - start;

  if (durationMs < 0) return 'N/A';

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const JobHistoryOverview: React.FC = () => {
  const {id: profileId, jobId, runId} = useParams<{ id: string; jobId: string; runId: string }>();
  const navigate = useNavigate();

  // Fetch profile data from API
  const {
    profile: profileData,
    loading: profileLoading,
    error: profileError
  } = useProfile(profileId || null);

  const [jobDetails, setJobDetails] = useState<JobRunDetails | null>(null);
  const [jobInfo, setJobInfo] = useState<JobInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [downloadingArtifacts, setDownloadingArtifacts] = useState(false);
  const [downloadingArtifactKey, setDownloadingArtifactKey] = useState<string | null>(null);
  const [copiedRunId, setCopiedRunId] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [showFullError, setShowFullError] = useState(false);

  useEffect(() => {
    loadJobInfo();
    loadJobRunDetails();
  }, [jobId, runId, jobDetails?.status]);


  // Load job information using GetJob API
  const loadJobInfo = async () => {
    if (!jobId) return;

    try {
      const response = await makeApiCall(API_PATHS.JOBS.GET, 'POST', {jobId});
      const responseData = response?.job;
      setJobInfo({
        jobId: responseData.jobId || jobId,
        jobName: responseData.jobName || 'Unknown Job',
        status: responseData.status || 'UNKNOWN',
        createdBy: responseData.createdBy || 'Unknown',
        createdAt: responseData.createdAt || '',
        updatedAt: responseData.updatedAt,
        ldap: responseData.ldap,
        jobType: responseData.jobType,
        mermaidDiagram: responseData.mermaidDiagram  // Store mermaid diagram from GetJob
      });
    } catch (error: any) {
      console.error('Failed to load job info:', error);
      // Don't block the page if job info fails
    }
  };

  // Load template data from S3 using templateUri
  const loadTemplateData = async (templateUri: string) => {
    try {

      // Get presigned URL for the template
      const {getPresignedDownloadUrl} = await import('../../utils/s3Download');
      const urlResponse = await getPresignedDownloadUrl(templateUri);

      if (!urlResponse?.downloadUrl) {
        console.error('Failed to get presigned URL for template');
        return;
      }

      // Fetch the template content
      const response = await fetch(urlResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.statusText}`);
      }

      const templateJson = await response.json();

      setTemplateData(templateJson);
    } catch (error: any) {
      console.error('Error loading template data:', error);
      // Don't block the page load if template fails
      setTemplateData(null);
    }
  };

  const loadJobRunDetails = async () => {
    if (!jobId || !runId) {
      setError('Missing job ID or run ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      NProgress.start();

      // Call GetJobRunStatus API
      const response = await makeApiCall(API_PATHS.JOBS.RUN_STATUS, 'POST', {
        jobId: jobId,
        runId: runId
      });


      // Map API response to our interface
      const jobRunDetails: JobRunDetails = {
        jobId: response.jobId || jobId,
        runId: response.runId || runId,
        jobName: response.jobName,
        status: response.status || 'RUNNING',
        runType: response.runType || 'NORMAL',
        startedAt: response.startedAt || new Date().toISOString(),
        updatedAt: response.completedAt,
        duration: calculateDuration(response.startedAt, response.completedAt),
        templateUri: response.templateUri,  // Store template S3 URI
        mermaidDiagram: response.mermaidDiagram,  // Store Mermaid diagram from backend
        createdBy: response.createdBy,
        ldap: response.ldap,
        feedback: response.feedback || '',
        executionArn: response.executionArn,
        codeGenerationOutput: response.codeGenerationOutput,
        errorMessage: response.errorMessage,
        currentAttempt: response.currentAttempt,
        executionHistory: response.executionHistory,
        executionStatus: response.executionStatus
      };

      setJobDetails(jobRunDetails);
      setFeedback(jobRunDetails.feedback || '');

      // If we have a workflow definition, parse it for visualization
      if (jobRunDetails.executionHistory) {
        try {
          const workflow = typeof jobRunDetails.executionHistory === 'string'
              ? JSON.parse(jobRunDetails.executionHistory)
              : jobRunDetails.executionHistory;
        } catch (e) {
          console.error('Failed to parse workflow definition:', e);
        }
      }

      // Load template data from S3 if templateUri is available
      if (response.templateUri) {
        await loadTemplateData(response.templateUri);
      } else {
        setTemplateData(null);
      }

    } catch (error: any) {
      console.error('Failed to load job run details:', error);
      setError(error.message || 'Failed to load job run details');
    } finally {
      setLoading(false);
      NProgress.done();
    }
  };
  const getOverallStatus = (status: JobRunStatus) => {
    // Check if it's a failure status
    if (isFailureStatus(status)) {
      return <StatusIndicator type="error">{status.replace(/_/g, ' ')}</StatusIndicator>;
    }

    // Handle specific statuses
    switch (status) {
      case 'COMPLETED':
        return <StatusIndicator type="success">Completed</StatusIndicator>;
      case 'SUCCEEDED':
        return <StatusIndicator type="success">Completed</StatusIndicator>;
      case 'PENDING':
        return <StatusIndicator type="pending">Pending</StatusIndicator>;
      case 'GENERATING_CODE':
        return <StatusIndicator type="loading">Generating Code</StatusIndicator>;
      case 'SUBMITTING_QUERY':
        return <StatusIndicator type="loading">Submitting Query</StatusIndicator>;
      case 'QUERY_SUBMITTED':
        return <StatusIndicator type="info">Query Submitted</StatusIndicator>;
      case 'QUERY_RUNNING':
        return <StatusIndicator type="in-progress">Query Running</StatusIndicator>;
      case 'CODE_RETRIEVED':
        return <StatusIndicator type="success">Code Retrieved</StatusIndicator>;
      case 'RETRYING':
        return <StatusIndicator type="warning">Retrying</StatusIndicator>;
      default:
        return <StatusIndicator type="pending">{status}</StatusIndicator>;
    }
  };

  const getBadgeColor = (status: JobRunStatus) => {
    // Failure statuses - red
    if (isFailureStatus(status)) {
      return 'red';
    }

    // Success statuses - green
    if (status === 'COMPLETED' || status === 'SUCCEEDED' || status === 'CODE_RETRIEVED') {
      return 'green';
    }

    // In-progress/loading statuses - blue
    if (status === 'GENERATING_CODE' || status === 'SUBMITTING_QUERY' || status === 'QUERY_RUNNING') {
      return 'blue';
    }

    // Retrying status - orange/yellow
    if (status === 'RETRYING') {
      return 'blue'; // AWS UI doesn't have orange, use blue
    }

    // Pending/submitted statuses - grey
    return 'grey';
  };

  // Copy job ID to clipboard
  const handleCopyRunId = () => {
    if (jobId) {
      navigator.clipboard.writeText(jobId);
      setCopiedRunId(true);
      setTimeout(() => setCopiedRunId(false), 2000);
    }
  };

  // Get truncated error message (first 5 lines or 300 characters)
  const getTruncatedError = (errorMessage: string) => {
    if (!errorMessage) {
      return {truncated: '', hasMore: false};
    }

    const lines = errorMessage.split('\n');

    // Check if message is long (more than 5 lines OR more than 300 characters)
    if (lines.length > 5) {
      return {
        truncated: lines.slice(0, 5).join('\n'),
        hasMore: true
      };
    }

    // If single/few lines but very long text
    if (errorMessage.length > 300) {
      return {
        truncated: errorMessage.substring(0, 300) + '...',
        hasMore: true
      };
    }

    return {truncated: errorMessage, hasMore: false};
  };

  // Handle rerun job with ADHOC run type
  const handleRerunJob = async () => {
    if (!jobId) {
      console.error('No job ID available for rerun');
      return;
    }

    setIsRerunning(true);
    setError(null);
    NProgress.start();

    try {
      const runJobPayload = {
        jobId,
        runType: 'ADHOC'
      };

      const response = await makeApiCall(API_PATHS.JOBS.RUN, 'POST', runJobPayload);

      // Show success message
      alert(`Job rerun started successfully! Run ID: ${response.runId || 'N/A'}`);

      // Navigate to the new run
      if (response.runId) {
        navigate(`/profile/${profileId}/job/${jobId}/run/${response.runId}`);
      } else {
        // If no runId, just refresh the current page
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Error rerunning job:', error);

      // Extract the actual error message from the response
      let errorMessage = 'Failed to rerun job. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      }

      // Set error to display in alert at top of page
      setError(errorMessage);
    } finally {
      setIsRerunning(false);
      NProgress.done();
    }
  };

  // Generate dropdown items from codeGenerationOutput
  const getArtifactDropdownItems = () => {
    if (!jobDetails?.codeGenerationOutput) return [];

    const output = jobDetails.codeGenerationOutput;
    const items = [];

    // Iterate through all keys in codeGenerationOutput
    for (const [key, value] of Object.entries(output)) {
      // Only include string values that look like S3 paths or file paths
      if (typeof value === 'string' && (value.startsWith('s3://') || value.includes('/'))) {
        items.push({
          id: key,
          text: key, // Use actual key from API response
          disabled: downloadingArtifactKey === key
        });
      }
    }

    return items;
  };

  const handleDownloadArtifact = async (artifactKey: string, artifactValue: string) => {
    if (!artifactValue) {
      return;
    }

    try {
      setDownloadingArtifactKey(artifactKey);

      // Get presigned URL for the artifact
      const {getPresignedDownloadUrl} = await import('../../utils/s3Download');

      // Extract S3 key from URI (handle both s3:// URIs and direct keys)
      let s3Key: string;
      if (artifactValue.startsWith('s3://')) {
        const match = artifactValue.match(/^s3:\/\/[^\/]+\/(.+)$/);
        if (!match || !match[1]) {
          throw new Error('Invalid S3 URI format');
        }
        s3Key = match[1];
      } else {
        s3Key = artifactValue;
      }

      const urlResponse = await getPresignedDownloadUrl(s3Key);

      if (!urlResponse?.downloadUrl) {
        throw new Error('Failed to get presigned URL for artifact');
      }

      // Fetch the artifact content
      const response = await fetch(urlResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch artifact: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Get the file extension from the S3 path or default based on content type
      const pathParts = artifactValue.split('/');
      const fileName = pathParts[pathParts.length - 1];
      let downloadFileName: string;

      if (fileName.includes('.')) {
        // Use the original filename if it has an extension
        downloadFileName = fileName;
      } else {
        // Determine extension from content type or default to .txt
        const contentType = response.headers.get('content-type') || '';
        let extension = '.txt';
        if (contentType.includes('json')) extension = '.json';
        else if (contentType.includes('xlsx')) extension = '.xlsx';
        else if (contentType.includes('csv')) extension = '.csv';
        else if (contentType.includes('sql')) extension = '.sql';

        downloadFileName = `${artifactKey}_${runId?.substring(0, 8) || 'download'}${extension}`;
      }

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);

    } catch (error: any) {
      console.error(`Failed to download artifact ${artifactKey}:`, error);
      setError(error.message || `Failed to download ${artifactKey}`);
    } finally {
      setDownloadingArtifactKey(null);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!jobDetails?.templateUri) {
      return;
    }

    try {
      NProgress.start();

      // Get presigned URL for the template
      const {getPresignedDownloadUrl} = await import('../../utils/s3Download');

      // Extract S3 key from URI
      const match = jobDetails.templateUri.match(/^s3:\/\/[^\/]+\/(.+)$/);
      if (!match || !match[1]) {
        throw new Error('Invalid S3 URI format');
      }

      const urlResponse = await getPresignedDownloadUrl(match[1]);

      if (!urlResponse?.downloadUrl) {
        throw new Error('Failed to get presigned URL for template');
      }

      // Fetch the template content
      const response = await fetch(urlResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `template-run-${runId || 'download'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      console.error('Failed to download template:', error);
      setError(error.message || 'Failed to download template');
    } finally {
      NProgress.done();
    }
  };

  if (loading) {
    return (
        <ContentLayout
            defaultPadding={true}
            headerVariant="high-contrast"
            header={
              <Box padding={{horizontal: "xxxl", vertical: "s"}}>
                <SpaceBetween size="m">
                  <AWSHeader variant="h1">Loading...</AWSHeader>
                </SpaceBetween>
              </Box>
            }
        >
          <Box padding={{horizontal: "xxxl"}}>
            <Container>
              <Box padding="xl" textAlign="center">
                <StatusIndicator type="loading">Loading job details...</StatusIndicator>
              </Box>
            </Container>
          </Box>
        </ContentLayout>
    );
  }

  return (
      <ContentLayout
          defaultPadding={false}
          headerVariant="high-contrast"
          header={
            <Box padding={{horizontal: "xxxl", vertical: "s"}}>
              <SpaceBetween size="m" direction="vertical">
                <BreadcrumbGroup
                    items={[
                      {text: 'Home', href: '/'},
                      {text: 'Profiles', href: '/'},
                      {text: profileData?.name || 'Loading...', href: `/profile/${profileId}`},
                      {text: 'Jobs', href: `/profile/${profileId}/jobs`},
                      {
                        text: jobDetails?.jobName || 'Job',
                        href: `/profile/${profileId}/job/${jobId}`
                      },
                      {text: 'History', href: `/profile/${profileId}/job/${jobId}/history`},
                      {
                        text: `Run ${jobDetails?.runId?.substring(0, 8) || runId?.substring(0, 8) || ''}...`,
                        href: '#'
                      }
                    ]}
                    ariaLabel="Breadcrumbs"
                />
                <Box>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px'
                      }}>
                        <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                          {jobInfo?.jobName ? `Job @${jobInfo?.jobName}` : 'Loading...'}
                        </Box>
                        {jobDetails && <Badge
                            color={getBadgeColor(jobDetails.status)}>{jobDetails.status}</Badge>}
                      </div>
                      <Box fontSize="body-m" color="inherit" margin={{top: "xs"}}>
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                          {jobDetails?.ldap && (
                              <>
                                <Badge color="green">{jobDetails.ldap}</Badge>
                                <Box color="text-status-inactive">|</Box>
                              </>
                          )}
                          {jobDetails?.runType && (
                              <>
                                <Badge color="blue">{jobDetails.runType}</Badge>
                                <Box color="text-status-inactive">|</Box>
                              </>
                          )}
                          {jobDetails?.createdBy && (
                              <>
                                <Link
                                    href={`https://phonetool.amazon.com/users/${jobDetails?.createdBy}`}
                                    external={true}
                                    fontSize="body-m"
                                >
                                  @{jobDetails?.createdBy}
                                </Link>
                                <Box color="text-status-inactive">|</Box>
                              </>
                          )}
                          <Box color="text-body-secondary">
                            Run ID: {runId?.substring(0, 8)}...
                          </Box>
                          <Button
                              variant="inline-icon"
                              iconName={copiedRunId ? "check" : "copy"}
                              onClick={handleCopyRunId}
                              ariaLabel="Copy run ID"
                          />
                          <Box color="text-status-inactive">|</Box>
                          <Box color="text-body-secondary">
                            Duration: {jobDetails?.duration ? jobDetails.duration : 'N/A'}
                          </Box>
                          <Box color="text-status-inactive">|</Box>
                          <Box color="text-body-secondary">
                            Started: {jobDetails?.startedAt ? new Date(jobDetails.startedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'}
                          </Box>
                        </SpaceBetween>
                      </Box>
                    </div>
                    <SpaceBetween direction="horizontal" size="xs">
                      <ButtonDropdown
                          items={getArtifactDropdownItems()}
                          onItemClick={({detail}) => {
                            const artifactKey = detail.id;
                            const artifactValue = jobDetails?.codeGenerationOutput?.[artifactKey];
                            if (artifactValue && typeof artifactValue === 'string') {
                              handleDownloadArtifact(artifactKey, artifactValue);
                            }
                          }}
                          disabled={!jobDetails?.codeGenerationOutput || getArtifactDropdownItems().length === 0 || downloadingArtifactKey !== null}
                          loading={downloadingArtifactKey !== null}
                      >
                        {downloadingArtifactKey ? `Downloading ${downloadingArtifactKey}...` : 'Download artifacts'}
                      </ButtonDropdown>
                      <Button
                          iconName="refresh"
                          variant="normal"
                          onClick={() => loadJobRunDetails()}
                          loading={loading}
                      >

                      </Button>
                      <Button
                          iconName="redo"
                          variant="primary"
                          onClick={handleRerunJob}
                          loading={isRerunning}
                          disabled={isRerunning}
                      >
                        Rerun
                      </Button>
                    </SpaceBetween>
                  </div>
                </Box>
              </SpaceBetween>
            </Box>
          }
      >
        <div className="job-history-content"
             style={{height: 'calc(100vh - 180px)', overflowY: 'auto', overflowX: 'hidden'}}>
          <Box padding={{horizontal: "xxxl", vertical: "l", bottom: "xl"}}>
            <SpaceBetween size="l">
              {/* Error Alert */}
              {error && (
                  <Alert type="warning" dismissible onDismiss={() => setError(null)}>
                    {error}
                  </Alert>
              )}

              {/* Feedback Information Banner */}
              <Alert
                  type="info"
                  dismissible={false}
                  header="Template Feedback"
                  action={
                      <Button
                          variant="primary"
                          iconName="edit"
                          onClick={() => {
                              console.log('Navigating to:', `/profile/${profileId}/job/${jobId}`);
                              navigate(`/profile/${profileId}/job/${jobId}`);
                          }}
                      >
                          Provide Feedback
                      </Button>
                  }
              >
                  Not satisfied with the job run results? You can improve the template by providing feedback.
              </Alert>

              {/* Job Overview Section */}
              <Container header={<AWSHeader variant="h2">Job run overview</AWSHeader>}>
                <SpaceBetween size="l">
                  {/* Show error message if job failed */}
                  {jobDetails?.errorMessage && jobDetails?.status && isFailureStatus(jobDetails.status) && (() => {
                    const {truncated, hasMore} = getTruncatedError(jobDetails.errorMessage);
                    return (
                        <Alert
                            type="error"
                            action={hasMore ? (
                                <Button
                                    onClick={() => setShowFullError(!showFullError)}
                                    variant="link"
                                >
                                  {showFullError ? 'Show less' : 'Show more'}
                                </Button>
                            ) : undefined}
                        >
                          <div>
                            <strong>Job Failed:</strong>
                            <pre style={{
                              marginTop: '8px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontFamily: 'Monaco, Menlo, monospace',
                              fontSize: '12px',
                              lineHeight: '1.5'
                            }}>
                                                {showFullError ? jobDetails.errorMessage : truncated}
                                            </pre>
                          </div>
                        </Alert>
                    );
                  })()}

                  <ColumnLayout columns={2} variant="text-grid">
                    <SpaceBetween size="l">
                      <div>
                        <Box variant="awsui-key-label">Start Time</Box>
                        <div>{jobDetails?.startedAt ? new Date(jobDetails.startedAt).toLocaleString() : 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">End Time</Box>
                        <div>{jobDetails?.updatedAt ? new Date(jobDetails.updatedAt).toLocaleString() : 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Created By</Box>
                        <div>
                          {jobDetails?.createdBy ? (
                              <Link
                                  href={`https://phonetool.amazon.com/users/${jobDetails.createdBy}`}
                                  external={true}
                              >
                                @{jobDetails.createdBy}
                              </Link>
                          ) : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">LDAP Group</Box>
                        <div>
                          {jobDetails?.ldap ? (
                              <Badge color="green">{jobDetails.ldap}</Badge>
                          ) : 'N/A'}
                        </div>
                      </div>
                    </SpaceBetween>
                    <SpaceBetween size="l">
                      <div>
                        <Box variant="awsui-key-label">Status</Box>
                        <div>{jobDetails && getOverallStatus(jobDetails.status)}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Duration</Box>
                        <div>{jobDetails?.duration || 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Step Function Execution Status</Box>
                        <div>{jobDetails?.executionStatus || 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Retry Attempt</Box>
                        <div>{jobDetails?.currentAttempt ? `${jobDetails.currentAttempt}` : 'N/A'}</div>
                      </div>
                    </SpaceBetween>
                  </ColumnLayout>
                </SpaceBetween>
              </Container>

                    {/* AWS Step Functions Visualization */}
                    <Container header={<AWSHeader variant="h2">Execution Flow</AWSHeader>}>
                        <Box padding="s" textAlign={'center'}>
                            {jobDetails?.executionHistory ? (() => {
                                try {
                                    // Parse the executionHistory JSON string
                                    const parsedHistory: ExecutonHistory = typeof jobDetails.executionHistory === 'string'
                                        ? JSON.parse(jobDetails.executionHistory)
                                        : jobDetails.executionHistory;

                                    // Extract definition and stateStatuses
                                    const definition = parsedHistory?.definition;
                                    const historyEvents = parsedHistory.executionHistory;

                                    if (!definition) {
                                        return (
                                            <Box textAlign="center" color="text-status-inactive" padding="s">
                                                <p>Workflow definition not available in execution history.</p>
                                            </Box>
                                        );
                                    }

                                    return (
                                        <StepFunctionsGraph
                                            historyEvents={historyEvents}
                                            stateMachineDefinition={definition}/>
                                    );
                                } catch (error) {
                                    return (
                                        <Alert type="error">
                                            Failed to parse workflow execution history: {error instanceof Error ? error.message : 'Unknown error'}
                                        </Alert>
                                    );
                                }
                            })() : (
                                <Box textAlign="center" color="text-status-inactive" padding="xl">
                                    <p>Workflow definition not available. Job may still be processing.</p>
                                </Box>
                            )}
                        </Box>
                    </Container>


              {/* Template Preview Section */}
              {jobDetails?.templateUri && (
                  <Container
                      header={
                        <AWSHeader
                            variant="h2"
                            actions={
                              <Button
                                  iconName="download"
                                  onClick={handleDownloadTemplate}
                                  disabled={!jobDetails?.templateUri}
                              >
                                Download template
                              </Button>
                            }
                        >
                          Template preview
                        </AWSHeader>
                      }
                  >
                    {templateData ? (
                        <JsonDataViewer
                            data={templateData}
                            mermaidDiagram={jobDetails?.mermaidDiagram || jobInfo?.mermaidDiagram}
                            aiSummary={jobDetails?.aiSummary || jobInfo?.aiSummary}
                            defaultView="grid"
                            style={{
                              maxHeight: '500px'
                            }}
                              collapsed={false}
                            displayObjectSize={true}
                              enableClipboard={true}
                          />
                    ) : (
                        <Box textAlign="center" padding="xl" color="text-status-inactive">
                          <SpaceBetween size="s" direction="vertical">
                            <Icon name="file" size="large"/>
                            <Box>
                              Template preview will appear here once loaded
                            </Box>
                          </SpaceBetween>
                        </Box>
                    )}
                  </Container>
              )}
            </SpaceBetween>
            <Box padding={{horizontal: "xxxl", vertical: "l", bottom: "xl"}}></Box>
          </Box>
        </div>
      </ContentLayout>
  );
};

export default JobHistoryOverview;